// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import Zone from "@insite/client-framework/Components/Zone";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button/Props";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CategoryItem, { CategoryItemStyles } from "@pacific/App/Components/Categories/CategoryItem";
import ViewMoreCategoriesCard, {
    ViewMoreCategoriesCardStyles,
} from "@pacific/App/Components/Categories/ViewMoreCategoryCard";
import XngCarousel from "@pacific/App/Components/XngCarousel/XngCarousel";
import setIsSendFeedbackModalOpen from "@pacific/App/Handlers/ProductList/SetIsSendFeedbackModalOpen";
import { CustomAppState } from "@pacific/App/Store/Reducers";
import Modal from "@pacific/MobiusOverrides/Modal/Modal";
import { SLIDES_PER_VIEW_POINTS } from "@pacific/XDL/constants";
import { color, generateSlidesPerViewMap, xScreen } from "@pacific/XDL/Utilities";
import parse from "html-react-parser";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    categoriesToShow = "categoriesToShow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.categoriesToShow]: number;
    };
}

const mapDispatchToProps = {
    setIsSendFeedbackModalOpen,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasCategoryContext;

interface SubCategoriesCarouselStyle {
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    categoryName?: TypographyProps;
    productsCount?: TypographyProps;
    productsCountTitle?: TypographyProps;
    categoryDescription?: TypographyProps;
    categoriesContainer?: InjectableCss;
    categoryItemStyles?: CategoryItemStyles;
    viewMoreCard?: ViewMoreCategoriesCardStyles;
    leaveUsFeedBackBtn?: ButtonPresentationProps;
    contactFormModal?: ModalPresentationProps;
}

export const centerSwiperSlideBreakPoint = 800;

const subCategoriesCarouselStyles: SubCategoriesCarouselStyle = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            margin-bottom: 32px;
            margin-top: 38px;
            align-items: flex-end;
            ${xScreen("lte", "md")} {
                margin-top: 0;
                margin-bottom: 56px;
            }
            ${xScreen("lte", "sm")} {
                margin-bottom: 48px;
            }
        `,
    },
    innerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            ${xScreen("lte", "md")} {
                margin-top: 24px;
            }
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            align-items: center;
            margin-bottom: 16px;
            width: 100%;
            flex-wrap: wrap;
            ${xScreen("lte", "sm")} {
                align-items: center;
            }
        `,
    },
    categoryName: {
        variant: "h1",
        css: css`
            margin-right: 32px;
            margin-bottom: 0;
            ${xScreen("lte", "sm")} {
                margin-bottom: 12px;
            }
        `,
    },
    productsCount: {
        size: 15,
        weight: 600,
        css: css`
            letter-spacing: 0;
            line-height: 21px;
        `,
    },
    productsCountTitle: {
        size: 15,
        weight: 500,
        css: css`
            letter-spacing: 0;
            line-height: 24px;
        `,
    },
    categoryDescription: {
        size: 15,
        weight: 500,
        css: css`
            letter-spacing: 0;
            line-height: 24px;
            margin-bottom: 32px;
            ${xScreen("lte", "sm")} {
                margin-bottom: 22px;
            }
        `,
    },
    categoriesContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            width: 100%;
            gap: 32px;
        `,
    },
    categoryItemStyles: {
        imageWrapper: {
            css: css`
                border: 1px solid ${color("common.ui5")};
                width: 200px;
                height: 200px;
                ${xScreen("lte", "md")} {
                    width: 168px;
                    height: 168px;
                }
                ${xScreen("lte", "sm")} {
                    width: 152px;
                    height: 152px;
                }
            `,
        },
        image: {
            width: 168,
            height: 168,
            css: css`
                object-fit: cover;
                width: 198px;
                height: 198px;
                img {
                    width: 100%;
                }
                ${xScreen("lte", "md")} {
                    width: 166px;
                    height: 166px;
                }
                ${xScreen("lte", "sm")} {
                    width: 152px;
                    height: 152px;
                }
            `,
        } as any,
        categoryTitle: {
            css: css`
                margin-top: 16px;
            `,
        },
    },
    viewMoreCard: {
        wrapper: {
            css: css`
                width: 168px;
                height: 168px;
                ${xScreen("lte", "sm")} {
                    width: 152px;
                    height: 152px;
                }
            `,
        },
    },
    contactFormModal: {
        sizeVariant: "large",
    },
};

const styles = subCategoriesCarouselStyles;

const breakpoints = generateSlidesPerViewMap({
    [SLIDES_PER_VIEW_POINTS.xs]: 2,
    [SLIDES_PER_VIEW_POINTS.sm]: 2,
    [SLIDES_PER_VIEW_POINTS.md]: 4,
    [SLIDES_PER_VIEW_POINTS.lg]: 6,
});

const SubCategoriesCarousel = ({ category, fields: { categoriesToShow }, id, setIsSendFeedbackModalOpen }: Props) => {
    const [expanded, setExpanded] = useState(false);
    const [subCategories, setSubCategories] = useState<string[]>([]);
    const { isMobileScreen, catalogPage, isSendFeedbackModalOpen } = useAppSelector(state => {
        const location = getLocation(state);
        const categoryPath =
            getSelectedCategoryPath(state) ||
            (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
        return {
            isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
            catalogPage: getCatalogPageStateByPath(state, categoryPath).value,
            isSendFeedbackModalOpen: (state as CustomAppState).xProductList.isSendFeedbackModalOpen,
        };
    });
    const handleModalOpen = () => {
        setIsSendFeedbackModalOpen({ isSendFeedbackModalOpen: true });
    };
    const handleModalClose = () => {
        setIsSendFeedbackModalOpen({ isSendFeedbackModalOpen: false });
    };

    const handleViewMoreClick = () => {
        setExpanded(!expanded);
    };

    useEffect(() => {
        if (expanded) {
            setSubCategories(category?.subCategoryIds || []);
            return;
        }
        setSubCategories(category?.subCategoryIds?.slice(0, categoriesToShow) || []);
    }, [categoriesToShow, expanded]);

    if (!category) {
        return null;
    }

    if (!category.subCategoryIds || !category.subCategoryIds?.length) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.innerWrapper}>
                    {/* <StyledWrapper {...styles.titleWrapper}>
                        <Typography {...styles.categoryName}>{category.name}</Typography>
                    </StyledWrapper> */}
                    {category.htmlContent && (
                        <Typography {...styles.categoryDescription}>
                            {parse(category.htmlContent, parserOptions)}
                        </Typography>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    const totalCount = category.subCategoryIds.length;
    const totalPrpoductsInCategory = (catalogPage as any)?.totalProductsCountInCategory;
    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.innerWrapper}>
                    {/* <StyledWrapper {...styles.titleWrapper}>
                        <Typography {...styles.categoryName}>{category.name}</Typography> */}
                    {/* TODO: make sure you put the right property for category number of products */}
                    {/* {catalogPage && (
                            <Typography {...styles.productsCount}>
                                {totalPrpoductsInCategory}&nbsp;
                                <Typography {...styles.productsCountTitle}>
                                    {translate("Product{0}", +totalPrpoductsInCategory > 1 ? "s" : "")}
                                </Typography>
                            </Typography>
                        )}
                    </StyledWrapper> */}
                    {category.htmlContent && (
                        <Typography {...styles.categoryDescription}>
                            {parse(category.htmlContent, parserOptions)}
                        </Typography>
                    )}
                    {isMobileScreen ? (
                        <XngCarousel spaceBetween={24} xSlidesPerView={true} breakpoints={breakpoints}>
                            {category.subCategoryIds?.map(subCategoryId => (
                                <CategoryItem
                                    key={subCategoryId}
                                    categoryId={subCategoryId}
                                    extendedStyles={styles.categoryItemStyles}
                                />
                            ))}
                        </XngCarousel>
                    ) : (
                        <StyledWrapper {...styles.categoriesContainer}>
                            {subCategories.map(subCategoryId => (
                                <CategoryItem
                                    key={subCategoryId}
                                    categoryId={subCategoryId}
                                    extendedStyles={styles.categoryItemStyles}
                                />
                            ))}
                            {totalCount > categoriesToShow && (
                                <ViewMoreCategoriesCard
                                    onClickHandler={handleViewMoreClick}
                                    text={
                                        expanded
                                            ? translate("Show Less")
                                            : translate("View More {0} Categories", `${totalCount - categoriesToShow}`)
                                    }
                                />
                            )}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <Modal isOpen={isSendFeedbackModalOpen} handleClose={handleModalClose} {...styles.contactFormModal}>
                <Zone contentId={id} zoneName="ContactForm"></Zone>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(withCategory(SubCategoriesCarousel)),
    definition: {
        group: "Categories",
        displayName: "Sub Categories Carousel",
        icon: "Carousel",
        allowedContexts: ["CategoryDetailsPage", "ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.categoriesToShow,
                displayName: "default number of categories to show",
                fieldType: "General",
                editorTemplate: "IntegerField",
                defaultValue: 4,
            },
        ],
    },
};

export default widgetModule;
